import React, { useState, useEffect } from 'react';
import HeaderButtonsForm from '../core/HeadersAndFooters/HeaderButtonsForm';
import HeaderContainer from '../core/HeadersAndFooters/HeaderContainer';
import FooterButtonsList from '../core/HeadersAndFooters/FooterButtonsList';
import FooterContainer from '../core/HeadersAndFooters/FooterContainer';
import ConfirmationModal from '../core/ConfirmationModal';
import ImageCropper from '../core/ImageCropper/ImageCropper';
import { Upload, Image as ImageIcon } from 'lucide-react';
import { useTranslation } from '../../i18n/useTranslation';

// Import protected Supabase client from shared location
import { supabase } from '../../lib/supabase';

const MyImages = ({ userId, onBack }) => {
  const { t } = useTranslation('user');
  const [userImages, setUserImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ visible: false, message: '', onOk: null });

  // Logo click listener - navigate back to MainFeed
  useEffect(() => {
    const handleLogoClick = () => {
      if (onBack) onBack();
    };
    window.addEventListener('ournet:context-switch', handleLogoClick);
    return () => window.removeEventListener('ournet:context-switch', handleLogoClick);
  }, [onBack]);

  // Image Cropper state
  const [showImageCropper, setShowImageCropper] = useState(false);
  const [selectedBucket, setSelectedBucket] = useState('hero-images');
  const [selectedAspectMode, setSelectedAspectMode] = useState('hero');
  const [showUploadOptions, setShowUploadOptions] = useState(false);

  // Load user's images from Supabase storage
  const loadUserImages = async () => {
    setLoading(true);
    try {
      console.log('Loading images for userId:', userId);

      // Get all images from hero-images bucket for this user
      const { data: heroImages, error: heroError } = await supabase.storage
        .from('hero-images')
        .list(`public/${userId}`, {
          limit: 100,
          sortBy: { column: 'created_at', order: 'desc' }
        });

      // Get all images from inline-images bucket for this user
      const { data: inlineImages, error: inlineError } = await supabase.storage
        .from('inline-images')
        .list(`public/${userId}`, {
          limit: 100,
          sortBy: { column: 'created_at', order: 'desc' }
        });

      if (heroError) console.error('Error loading hero images:', heroError);
      if (inlineError) console.error('Error loading inline images:', inlineError);

      console.log('Hero images found:', heroImages?.length || 0);
      console.log('Inline images found:', inlineImages?.length || 0);

      // Combine and format images
      const allImages = [];

      if (heroImages) {
        heroImages.forEach(img => {
          if (img.name && !img.name.includes('_thumb') && !img.name.includes('-thumb')) {
            const { data: urlData } = supabase.storage
              .from('hero-images')
              .getPublicUrl(`public/${userId}/${img.name}`);

            allImages.push({
              id: img.id,
              name: img.name,
              url: urlData.publicUrl,
              bucket: 'hero-images',
              size: img.metadata?.size,
              created_at: img.created_at
            });
          }
        });
      }

      if (inlineImages) {
        inlineImages.forEach(img => {
          if (img.name && !img.name.includes('_thumb') && !img.name.includes('-thumb')) {
            const { data: urlData } = supabase.storage
              .from('inline-images')
              .getPublicUrl(`public/${userId}/${img.name}`);

            allImages.push({
              id: img.id,
              name: img.name,
              url: urlData.publicUrl,
              bucket: 'inline-images',
              size: img.metadata?.size,
              created_at: img.created_at
            });
          }
        });
      }

      setUserImages(allImages);
    } catch (error) {
      console.error('Error loading user images:', error);
      setModal({
        visible: true,
        message: t('my_images_error_load'),
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
    } finally {
      setLoading(false);
    }
  };

  // Delete user image
  const handleDeleteImage = async (image) => {
    try {
      const { error } = await supabase.storage
        .from(image.bucket)
        .remove([`public/${userId}/${image.name}`]);

      if (error) throw error;

      // Also try to delete thumbnail
      const thumbName = image.name.replace(/(\.[^.]+)$/, '_thumb$1');
      await supabase.storage
        .from(image.bucket)
        .remove([`public/${userId}/${thumbName}`]);

      // Reload images
      loadUserImages();

      setModal({
        visible: true,
        message: t('my_images_delete_success'),
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
    } catch (error) {
      console.error('Error deleting image:', error);
      setModal({
        visible: true,
        message: t('my_images_error_delete'),
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
    }
  };

  // Handle image upload from ImageCropper
  const handleImageSave = ({ displayUrl, thumbnailUrl }) => {
    console.log('Image uploaded:', { displayUrl, thumbnailUrl, bucket: selectedBucket });

    setModal({
      visible: true,
      message: t('my_images_upload_success'),
      onOk: () => setModal({ visible: false, message: '', onOk: null })
    });

    // Reload images to show the new upload
    loadUserImages();
  };

  // Open upload options
  const handleUploadClick = () => {
    setShowUploadOptions(true);
  };

  // Start upload with selected options
  const startUpload = (bucket, aspectMode) => {
    setSelectedBucket(bucket);
    setSelectedAspectMode(aspectMode);
    setShowUploadOptions(false);
    setShowImageCropper(true);
  };

  useEffect(() => {
    if (userId) {
      loadUserImages();
    }
  }, [userId]);

  return (
    <div style={{ minHeight: '100vh', paddingTop: '90px', paddingBottom: '120px' }}>
      <HeaderContainer />
      <HeaderButtonsForm
        headerTitle={t('my_images_title')}
        onBack={onBack}
      />

      <div style={{ paddingLeft: '40px', paddingRight: '40px' }}>
        {/* Upload Button */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-start',
          marginBottom: '20px'
        }}>
          <button
            onClick={handleUploadClick}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              backgroundColor: '#BB4D9A',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#9a3d7e'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#BB4D9A'}
          >
            <Upload size={18} />
            {t('my_images_upload_button')}
          </button>
        </div>

        {loading ? (
          <div style={{
            textAlign: 'center',
            padding: '30px 10px',
            color: '#6b7280'
          }}>
            <div style={{
              fontSize: '24px',
              marginBottom: '8px'
            }}>
              {t('my_images_loading')}
            </div>
          </div>
        ) : userImages.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: '#6b7280',
            backgroundColor: '#f9fafb',
            borderRadius: '12px',
            border: '2px dashed #e5e7eb'
          }}>
            <ImageIcon size={48} color="#BB4D9A" style={{ marginBottom: '16px' }} />
            <p style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
              {t('my_images_empty_title')}
            </p>
            <p style={{ fontSize: '14px', maxWidth: '400px', margin: '0 auto 20px' }}>
              {t('my_images_empty_description')}
            </p>
            <button
              onClick={handleUploadClick}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 24px',
                backgroundColor: '#BB4D9A',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              <Upload size={18} />
              {t('my_images_upload_first')}
            </button>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(125px, 1fr))',
            gap: '10px'
          }}>
            {userImages.map((image) => (
              <div key={image.id} style={{
                position: 'relative',
                borderRadius: '6px',
                overflow: 'hidden',
                border: '1px solid #e5e7eb',
                backgroundColor: 'white',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                transition: 'transform 0.2s, box-shadow 0.2s',
                aspectRatio: '1 / 1'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.15)';
                const deleteBtn = e.currentTarget.querySelector('.delete-btn-container');
                if (deleteBtn) deleteBtn.style.opacity = '1';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.1)';
                const deleteBtn = e.currentTarget.querySelector('.delete-btn-container');
                if (deleteBtn) deleteBtn.style.opacity = '0';
              }}>
                {/* Image */}
                <img
                  src={image.url}
                  alt={image.name}
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    backgroundColor: '#f3f4f6'
                  }}
                />

                {/* Delete button overlay on hover */}
                <div style={{
                  position: 'absolute',
                  bottom: '4px',
                  right: '4px',
                  opacity: 0,
                  transition: 'opacity 0.2s'
                }}
                className="delete-btn-container"
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm(t('my_images_delete_confirm', { name: image.name }))) {
                        handleDeleteImage(image);
                      }
                    }}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: 'rgba(220, 38, 38, 0.9)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '10px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    {t('my_images_delete_button')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmationModal
        visible={modal.visible}
        title={t('my_images_title')}
        message={modal.message}
        okLabel={t('common:ok')}
        onOk={() => {
          try { modal.onOk && modal.onOk(); } catch (e) { console.error(e); setModal({ visible: false, message: '', onOk: null }); }
        }}
      />

      {/* Upload Options Modal */}
      {showUploadOptions && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 9998,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        onClick={() => setShowUploadOptions(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '24px',
              width: '90%',
              maxWidth: '400px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#374151',
              marginBottom: '8px',
              textAlign: 'center'
            }}>
              {t('my_images_choose_type_title')}
            </h3>
            <p style={{
              fontSize: '14px',
              color: '#6b7280',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              {t('my_images_choose_type_description')}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Hero Image Option */}
              <button
                onClick={() => startUpload('hero-images', 'hero')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '16px',
                  backgroundColor: '#f9fafb',
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#BB4D9A';
                  e.currentTarget.style.backgroundColor = '#fdf2f8';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                }}
              >
                <div style={{
                  width: '48px',
                  height: '36px',
                  backgroundColor: '#BB4D9A',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <ImageIcon size={20} color="white" />
                </div>
                <div>
                  <p style={{ fontSize: '16px', fontWeight: '600', color: '#374151', margin: 0 }}>
                    {t('my_images_hero_image_title')}
                  </p>
                  <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>
                    {t('my_images_hero_image_description')}
                  </p>
                </div>
              </button>

              {/* Inline Image Option */}
              <button
                onClick={() => startUpload('inline-images', 'inline')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '16px',
                  backgroundColor: '#f9fafb',
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#BB4D9A';
                  e.currentTarget.style.backgroundColor = '#fdf2f8';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                }}
              >
                <div style={{
                  width: '36px',
                  height: '36px',
                  backgroundColor: '#024B99',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <ImageIcon size={20} color="white" />
                </div>
                <div>
                  <p style={{ fontSize: '16px', fontWeight: '600', color: '#374151', margin: 0 }}>
                    {t('my_images_inline_image_title')}
                  </p>
                  <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>
                    {t('my_images_inline_image_description')}
                  </p>
                </div>
              </button>
            </div>

            {/* Cancel Button */}
            <button
              onClick={() => setShowUploadOptions(false)}
              style={{
                width: '100%',
                marginTop: '16px',
                padding: '12px',
                backgroundColor: 'transparent',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#6b7280',
                cursor: 'pointer'
              }}
            >
              {t('common:cancel')}
            </button>
          </div>
        </div>
      )}

      {/* Image Cropper Modal */}
      <ImageCropper
        isOpen={showImageCropper}
        onClose={() => setShowImageCropper(false)}
        onSave={handleImageSave}
        aspectMode={selectedAspectMode}
        bucket={selectedBucket}
        userId={userId}
      />

      <FooterContainer viewType="list">
        <FooterButtonsList />
      </FooterContainer>
    </div>
  );
};

export default MyImages;
